import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, Gift } from "lucide-react";
import { api, errMsg, COUNTRIES, OTP_KEY, REF_KEY } from "../../lib/api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const SOURCES = ["Instagram", "YouTube", "WhatsApp", "Friends or Teammates", "Cricket Academy or Coach", "Google Search", "LinkedIn", "Other"];
const EMPTY = { full_name: "", email: "", country: "IN", phone: "", age: "", referral_source: "", referral_source_other: "", marketing_consent: false, guardian_consent: false, terms_accepted: false, website: "" };

const validate = (f) => {
  const e = {};
  if (f.full_name.trim().length < 2) e.full_name = "Please enter your full name.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = "Please enter a valid email address.";
  if (f.phone.replace(/\D/g, "").length < 6) e.phone = "Please enter a valid phone number.";
  const age = Number(f.age);
  if (!age || age < 5 || age > 100) e.age = "Please enter an age between 5 and 100.";
  if (!f.referral_source) e.referral_source = "Please tell us how you heard about CricAnalyze.";
  if (age && age < 18 && !f.guardian_consent) e.guardian_consent = "A parent or guardian's permission is required.";
  if (!f.terms_accepted) e.terms_accepted = "Please accept the Terms and Privacy Policy.";
  return e;
};

const Err = ({ k, errors }) => errors[k] ? <p className="text-xs text-red-400 mt-1.5" data-testid={`error-${k}`}>{errors[k]}</p> : null;

export const WaitlistForm = () => {
  const nav = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [ref, setRef] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const [params] = useSearchParams();
  const urlRef = params.get("ref");
  useEffect(() => {
    const fromUrl = urlRef && /^[A-Za-z0-9]{4,20}$/.test(urlRef) ? urlRef.toUpperCase() : null;
    if (fromUrl) sessionStorage.setItem(REF_KEY, fromUrl);
    const code = fromUrl || sessionStorage.getItem(REF_KEY);
    setRef(null);
    if (code) api.get(`/public/referral/${code}`).then((r) => r.data.valid ? setRef(code) : sessionStorage.removeItem(REF_KEY)).catch(() => {});
  }, [urlRef]);

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(f);
    setErrors(e);
    if (Object.keys(e).length) return;
    setLoading(true);
    try {
      const dial = COUNTRIES.find((c) => c[0] === f.country)[1];
      const phone = f.phone.trim().startsWith("+") ? f.phone.trim() : `${dial}${f.phone.replace(/^0+/, "")}`;
      const { data } = await api.post("/waitlist/register", { ...f, phone, age: Number(f.age), ref, email: f.email.trim() });
      sessionStorage.setItem(OTP_KEY, JSON.stringify({ challenge_id: data.challenge_id, email: data.email }));
      toast.success(data.message);
      nav("/verify-email");
    } catch (err) {
      toast.error(errMsg(err));
      setErrors({ form: errMsg(err) });
    } finally { setLoading(false); }
  };

  const inputCls = "h-12 bg-[#080A08] border-[#2A362A] focus-visible:ring-[#B7FF3C]/60 text-white placeholder:text-[#5E6B5E]";
  const minor = Number(f.age) > 0 && Number(f.age) < 18;

  return (
    <form onSubmit={submit} noValidate className="card-surface lime-glow p-6 sm:p-9 space-y-5" data-testid="waitlist-form">
      <div>
        <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-tight">Join the CricAnalyze Waitlist.</h2>
        <p className="mt-2 text-[#A3ADA3]">Be among the first to experience a smarter approach to cricket performance.</p>
      </div>
      {ref && <div className="flex items-center gap-2 text-sm rounded-lg border border-[#B7FF3C]/30 bg-[#B7FF3C]/5 px-4 py-2.5" data-testid="referral-badge"><Gift size={16} className="text-[#B7FF3C]" /> You were invited with referral code <span className="font-mono text-[#B7FF3C]">{ref}</span></div>}
      <input type="text" name="website" value={f.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <div>
        <Label htmlFor="full_name">Full name</Label>
        <Input id="full_name" data-testid="input-full-name" className={`${inputCls} mt-2`} value={f.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="e.g. Arjun Sharma" />
        <Err k="full_name" errors={errors} />
      </div>
      <div>
        <Label htmlFor="email">Email address</Label>
        <Input id="email" type="email" data-testid="input-email" className={`${inputCls} mt-2`} value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" />
        <Err k="email" errors={errors} />
      </div>
      <div>
        <Label>Phone number</Label>
        <div className="mt-2 flex gap-2">
          <Select value={f.country} onValueChange={(v) => set("country", v)}>
            <SelectTrigger className={`${inputCls} w-[132px]`} data-testid="select-country"><SelectValue /></SelectTrigger>
            <SelectContent>{COUNTRIES.map(([iso, dial, name]) => <SelectItem key={iso} value={iso} data-testid={`country-${iso}`}>{iso} {dial} · {name}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="tel" data-testid="input-phone" className={`${inputCls} flex-1`} value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="98765 43210" />
        </div>
        <Err k="phone" errors={errors} />
      </div>
      <div className="grid sm:grid-cols-[120px_1fr] gap-4">
        <div>
          <Label htmlFor="age">Age</Label>
          <Input id="age" type="number" min="5" max="100" data-testid="input-age" className={`${inputCls} mt-2`} value={f.age} onChange={(e) => set("age", e.target.value)} />
          <Err k="age" errors={errors} />
        </div>
        <div>
          <Label>How did you hear about CricAnalyze?</Label>
          <Select value={f.referral_source} onValueChange={(v) => set("referral_source", v)}>
            <SelectTrigger className={`${inputCls} mt-2`} data-testid="select-referral-source"><SelectValue placeholder="Select an option" /></SelectTrigger>
            <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s} data-testid={`source-${s.toLowerCase().replace(/ /g, "-")}`}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Err k="referral_source" errors={errors} />
        </div>
      </div>
      {f.referral_source === "Other" && (
        <Input data-testid="input-source-other" className={inputCls} value={f.referral_source_other} onChange={(e) => set("referral_source_other", e.target.value)} placeholder="Tell us where (optional)" maxLength={120} />
      )}
      {minor && (
        <label className="flex gap-3 items-start text-sm text-[#C9D1C9] rounded-lg border border-[#2A362A] p-3">
          <Checkbox data-testid="checkbox-guardian" checked={f.guardian_consent} onCheckedChange={(v) => set("guardian_consent", !!v)} className="mt-0.5" />
          <span>I confirm my parent or guardian has given permission for me to join the CricAnalyze waitlist.</span>
        </label>
      )}
      <Err k="guardian_consent" errors={errors} />
      <label className="flex gap-3 items-start text-sm text-[#A3ADA3]">
        <Checkbox data-testid="checkbox-marketing" checked={f.marketing_consent} onCheckedChange={(v) => set("marketing_consent", !!v)} className="mt-0.5" />
        <span>I agree to receive product news, launch announcements, offers, and promotional emails from CricAnalyze. I can unsubscribe at any time. <span className="text-[#5E6B5E]">(Optional)</span></span>
      </label>
      <label className="flex gap-3 items-start text-sm text-[#A3ADA3]">
        <Checkbox data-testid="checkbox-terms" checked={f.terms_accepted} onCheckedChange={(v) => set("terms_accepted", !!v)} className="mt-0.5" />
        <span>I agree to the <a href="/terms" target="_blank" className="text-[#B7FF3C] hover:underline">Terms &amp; Reward Conditions</a> and <a href="/privacy" target="_blank" className="text-[#B7FF3C] hover:underline">Privacy Policy</a>.</span>
      </label>
      <Err k="terms_accepted" errors={errors} />
      {errors.form && <p className="text-sm text-red-400 rounded-lg border border-red-500/30 bg-red-500/5 p-3" data-testid="form-error">{errors.form}</p>}
      <button type="submit" disabled={loading} data-testid="waitlist-submit-btn" className="w-full h-13 py-3.5 rounded-full bg-[#B7FF3C] text-[#080A08] font-bold text-base flex items-center justify-center gap-2 hover:bg-[#c9ff6a] transition-colors disabled:opacity-60">
        {loading ? <Loader2 className="animate-spin" size={18} /> : <>Join the Waitlist <ArrowRight size={18} /></>}
      </button>
    </form>
  );
};
